import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getStoryWithMatches, submitDebugStory } from '../api/client';
import type { StoryResponse, MatchResponse, DebugStoryResult } from '../api/types';
import ScoreBreakdown from '../components/scores/ScoreBreakdown';
import DecisionFlow from '../components/pipeline/DecisionFlow';
import PromptInspector from '../components/prompts/PromptInspector';

export default function StoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [story, setStory] = useState<StoryResponse | null>(null);
  const [matches, setMatches] = useState<MatchResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debug analysis state
  const [debugResult, setDebugResult] = useState<DebugStoryResult | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugError, setDebugError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    getStoryWithMatches(id)
      .then((res) => {
        setStory(res.story);
        setMatches(res.matches);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const runDebugAnalysis = async () => {
    if (!story) return;
    setDebugLoading(true);
    setDebugError(null);
    try {
      const result = await submitDebugStory(
        story.rawText,
        story.location.city,
        undefined, // use default config
        true // skipPersist - don't create duplicate
      );
      setDebugResult(result);
    } catch (e) {
      setDebugError(e instanceof Error ? e.message : 'Failed to run debug analysis');
    } finally {
      setDebugLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error: {error}
      </div>
    );
  }

  if (!story) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/stories" className="text-pebbl-600 hover:text-pebbl-700">
          &larr; Back
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Story Detail</h1>
      </div>

      {/* Story Card */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm font-medium px-2 py-0.5 rounded bg-pebbl-100 text-pebbl-700">
            {story.type}
          </span>
          <span className="text-sm text-gray-500">{story.location.city}</span>
          {story.location.venue && (
            <span className="text-sm text-gray-500">@ {story.location.venue}</span>
          )}
        </div>

        <p className="text-gray-700 mb-4 whitespace-pre-wrap">{story.rawText}</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm border-t pt-4">
          <div>
            <div className="text-gray-500">Date</div>
            <div>{story.time.date || 'Not specified'}</div>
          </div>
          <div>
            <div className="text-gray-500">Time</div>
            <div>{story.time.time || story.time.dayPart || 'Not specified'}</div>
          </div>
          <div>
            <div className="text-gray-500">Trigger</div>
            <div>{story.event.trigger || 'None'}</div>
          </div>
          <div>
            <div className="text-gray-500">Object</div>
            <div>{story.event.object || 'None'}</div>
          </div>
        </div>

        {story.themProfile.descriptors.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <div className="text-sm text-gray-500 mb-2">Person Descriptors</div>
            <div className="flex flex-wrap gap-1">
              {story.themProfile.descriptors.map((d, i) => (
                <span key={i} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                  {d}
                </span>
              ))}
            </div>
          </div>
        )}

        {story.location.microCues.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <div className="text-sm text-gray-500 mb-2">Micro Cues</div>
            <div className="flex flex-wrap gap-1">
              {story.location.microCues.map((c, i) => (
                <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Matches */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Matches ({matches.length})
        </h2>

        {matches.length === 0 ? (
          <div className="text-gray-500">No matches found for this story.</div>
        ) : (
          <div className="space-y-4">
            {matches.map((match) => (
              <Link
                key={match.id}
                to={`/matches/${match.id}`}
                className="card block hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                      match.status === 'confirmed'
                        ? 'bg-green-100 text-green-700'
                        : match.status === 'rejected'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {match.status}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-pebbl-600">
                      {((match.scores.final ?? match.scores.overall) * 100).toFixed(0)}%
                    </div>
                    <div className="text-xs text-gray-500">final score</div>
                  </div>
                </div>

                <div className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {match.storyB.snippet}
                </div>

                <ScoreBreakdown scores={match.scores} compact />

                {match.explanation.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="text-xs text-gray-500">Why this matched:</div>
                    <ul className="text-sm text-gray-600 mt-1">
                      {match.explanation.slice(0, 3).map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Debug Analysis Section */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Pipeline Debug Analysis</h2>
          <button
            onClick={runDebugAnalysis}
            disabled={debugLoading}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            {debugLoading ? 'Analyzing...' : debugResult ? 'Re-run Analysis' : 'Run Analysis'}
          </button>
        </div>

        {debugError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4">
            {debugError}
          </div>
        )}

        {!debugResult && !debugLoading && (
          <div className="text-gray-500 text-sm">
            Click "Run Analysis" to see how this story flows through the matching pipeline,
            including vector search results, gate evaluations, and the Claude parsing prompt.
          </div>
        )}

        {debugResult && (
          <div className="space-y-6">
            {/* Pipeline Flow */}
            <DecisionFlow traces={debugResult.pipelineTrace} />

            {/* Candidates Table */}
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-4">
                All Candidates ({debugResult.candidates.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2">Story ID</th>
                      <th className="text-left p-2">Overall</th>
                      <th className="text-left p-2">Event</th>
                      <th className="text-left p-2">Location</th>
                      <th className="text-left p-2">Time</th>
                      <th className="text-left p-2">Gates</th>
                      <th className="text-left p-2">Filtered At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debugResult.candidates.map((c) => (
                      <tr key={c.storyId} className="border-t hover:bg-gray-50">
                        <td className="p-2">
                          <Link
                            to={`/stories/${c.storyId}`}
                            target="_blank"
                            className="font-mono text-xs text-pebbl-600 hover:underline"
                          >
                            {c.storyId}
                          </Link>
                        </td>
                        <td className="p-2 font-medium">
                          {(c.detailedScores.overall * 100).toFixed(0)}%
                        </td>
                        <td className="p-2">
                          <span className={c.gateResults.eventPass ? 'text-green-600' : 'text-red-600'}>
                            {(c.detailedScores.event * 100).toFixed(0)}%
                          </span>
                        </td>
                        <td className="p-2">
                          <span className={c.gateResults.locationPass ? 'text-green-600' : 'text-red-600'}>
                            {(c.detailedScores.location * 100).toFixed(0)}%
                          </span>
                        </td>
                        <td className="p-2">
                          <span className={c.gateResults.timePass ? 'text-green-600' : 'text-red-600'}>
                            {(c.detailedScores.time * 100).toFixed(0)}%
                          </span>
                        </td>
                        <td className="p-2">{c.gateResults.coverageCount}/4</td>
                        <td className="p-2">
                          {c.filteredAt ? (
                            <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                              {c.filteredAt}
                            </span>
                          ) : (
                            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                              passed
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Prompt Inspector */}
            <PromptInspector prompts={debugResult.prompts} />

            <div className="text-xs text-gray-500 text-right">
              Total analysis time: {debugResult.totalLatencyMs}ms
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
